import React from 'react';
import { motion } from 'framer-motion';
import { useApi } from '../hooks/useApi';
import { apiService, getCurrentLocale } from '../services/api';
import { getBestImageUrl } from '../utils/imageUrl';
import fallbackLocation from '../assets/images/205d904e1a2de7595526a2f1287fb9326166fa06.png';

interface LocationAboutData {
  id: number;
  documentId: string;
  title: string;
  description?: string;
  content: string;
  mission?: string;
  vision?: string;
  values?: string;
  images?: {
    id: number;
    documentId: string;
    name: string;
    url: string;
    alternativeText?: string;
    caption?: string;
    width: number;
    height: number;
    formats?: {
      large?: { url: string; width: number; height: number; size: number; };
      medium?: { url: string; width: number; height: number; size: number; };
      small?: { url: string; width: number; height: number; size: number; };
      thumbnail?: { url: string; width: number; height: number; size: number; };
    };
    hash: string;
    ext: string;
    mime: string;
    size: number;
    previewUrl?: string;
    provider: string;
    provider_metadata?: any;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  }[];
  ctaButton?: any;
  seo?: any;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

const LocationSection: React.FC = () => {
  const locale = getCurrentLocale();
  const { data, loading, error } = useApi(apiService.getLocationAboutContent, [locale]);
  
  // Cast data to proper type
  const locationData = data as LocationAboutData | null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  // Debug logging for location about data
  if (locationData && !loading && !error) {
    console.log('📝 LocationSection - ✅ Strapi data loaded:', !!locationData);
    console.log('📝 LocationSection - Data keys:', Object.keys(locationData || {}));
    console.log('📝 LocationSection - Title:', locationData.title);
    console.log('📝 LocationSection - Content length:', locationData.content?.length || 0);
    console.log('📝 LocationSection - Has images:', !!locationData.images);
    console.log('📝 LocationSection - Images count:', locationData.images?.length || 0);
    if (locationData.images && locationData.images.length > 0) {
      console.log('📝 LocationSection - First image:', locationData.images[0].name);
      console.log('📝 LocationSection - Image URL:', locationData.images[0].url);
    }
  }

  if (loading) {
    console.log('📝 LocationSection - Loading Strapi data...');
  }

  if (error) {
    console.log('📝 LocationSection - ❌ Error loading Strapi data:', error);
    console.log('📝 LocationSection - Falling back to default content');
  }

  // Default content when no Strapi data is available
  const defaultContentByLocale: Record<'nl' | 'ar', string> = {
    nl: 'Gevestigd in Noord-Holland. Laagdrempelig en privé. Onze praktijk is rustig gelegen, zonder grote wachtkamer of voorbijgangers. Afspraken verlopen eenvoudig via WhatsApp of online. Je kunt bij ons terecht vanaf 25 jaar, ongeacht je achtergrond of eerdere ervaringen.',
    ar: 'نحن في شمال هولندا بأجواء خاصة وهادئة. يسهل حجز المواعيد عبر واتساب أو عبر الإنترنت. نرحب بالعملاء من سن 25 عامًا فما فوق.'
  };

  if (loading) {
    return (
      <section className="about section-padding">
        <div className="about-content">
          <div className="about-text">
            <div className="loading-placeholder">
              <div className="loading-title"></div>
              <div className="loading-text"></div>
            </div>
          </div>
          <div className="about-image">
            <div className="loading-placeholder">Loading image...</div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="about section-padding">
        <div className="about-content">
          <div className="about-text">
            <h2>{'Onze locatie'}</h2>
            <div 
              className="about-content-location" 
              dangerouslySetInnerHTML={{ __html: defaultContentByLocale[locale] }}
            />
          </div>
          <div className="about-image">
            <div className="loading-placeholder">Loading image...</div>
          </div>
        </div>
      </section>
    );
  }

  const title = locationData?.title || 'Onze locatie';
  // Prefer rich content; if empty, fall back to description; if still missing, use localized default
  const contentHtml = (locationData?.content && locationData.content.trim().length > 0
    ? locationData.content
    : (locationData?.description && locationData.description.trim().length > 0
      ? locationData.description
      : defaultContentByLocale[locale]));

  // Get best available image URL from Strapi (use first image). Fallback to local image if none.
  const strapiImageUrl = locationData?.images?.[0] ? getBestImageUrl(locationData.images[0]) : null;
  const imageUrl = strapiImageUrl || fallbackLocation;

  return (
    <motion.section
      className="about section-padding"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="about-content">
        <motion.div className="about-text" variants={itemVariants} style={{ textAlign: locale === 'ar' ? 'right' : 'left' }}>
          <h2 className="location-title">{title}</h2>
          <div 
            className="about-content-location" 
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />
        </motion.div>
        <motion.div className="location-image" variants={itemVariants}>
          <img 
            src={imageUrl}
            alt={locationData?.images?.[0]?.alternativeText || locationData?.images?.[0]?.caption || 'Clinic Location'} 
            className="location-img"
          />
        </motion.div>
      </div>
    </motion.section>
  );
};

export default LocationSection; 